import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  Action,
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceSchemaVersion,
  CaraWorkspaceState,
  Resource,
  type VisibilityConfigInput,
  UpdateCaraWorkspacesDocument,
  type UpdateCaraWorkspacesMutation
} from '~/api/graphql';
import { usePermissions } from '~/components/permissions';
import {
  GenerateSmartFillBucketDialog,
  useSmartFillBucketGeneration
} from '~/components/smart-fill/generate-bucket-dialog';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';

import { useArchiveCaraWorkspace } from '~/components/workspaces/hooks/use-archive-cara-workspace';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { useRenameCaraWorkspace } from '~/components/workspaces/hooks/use-rename-cara-workspace';
import { useWorkspaceRunTimer } from '~/components/workspaces/hooks/use-workspace-run-timer';
import { ConvertToPresetWizard } from './convert-to-preset-wizard';
import { WorkspaceTopBarActions } from './workspace-top-bar-actions';
import { WorkspaceTopBarArchiveDialog } from './workspace-top-bar-archive-dialog';
import { WorkspaceTopBarStatus } from './workspace-top-bar-status';
import { WorkspaceTopBarTitle } from './workspace-top-bar-title';
import { WorkspaceClientPicker } from './workspace-client-picker';

const CREATE_CUSTOM_PRESET = [{ resource: Resource.CustomPreset, action: Action.Create }];
const CREATE_SMART_FILL_BUCKET = [{ resource: Resource.SmartFill, action: Action.Create }];

/**
 * Top bar of the workspace page. Composes:
 *
 *   [back]  [preset icon] [title + metadata]     [client] [status] [stop?] [⋯]
 *
 * Client, status, stop, rename, and archive each live in their own small
 * component or hook so this file stays a thin wrapper.
 */
export const WorkspaceTopBar = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  setWorkspace: (workspace: CaraWorkspaceFieldsFragment) => void;
  /** False when the caller may read this workspace but not change it. */
  canUpdate: boolean;
  onStop: () => void;
  stopLoading: boolean;
}) => {
  const { workspace } = props;

  const rename = useRenameCaraWorkspace({ workspace, setWorkspace: props.setWorkspace });
  const archive = useArchiveCaraWorkspace({ workspace });
  const timer = useWorkspaceRunTimer(workspace);
  const visibilityEditAllowed = useVisibilityEditAllowed(workspace.agencyUser?.id);
  const convertToPresetEnabled = useFlag(FEATURE_FLAGS.CONVERT_WORKSPACE_TO_PRESET);
  const smartFillEnabled = useFlag(FEATURE_FLAGS.SMART_FILL);
  const smartFillBucketsEnabled = useFlag(FEATURE_FLAGS.SMART_FILL_ACORD_BUCKETS);
  const permissions = usePermissions();
  // Converting writes a preset, not the workspace, so it carries its own permission.
  const canCreatePreset = permissions.hasAllPermissions(CREATE_CUSTOM_PRESET);
  const canGenerateSmartFillBucket =
    smartFillEnabled &&
    smartFillBucketsEnabled &&
    permissions.hasAllPermissions(CREATE_SMART_FILL_BUCKET);

  const [renameRequestNonce, setRenameRequestNonce] = React.useState(0);
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);
  const [showConvertDialog, setShowConvertDialog] = React.useState(false);
  const smartFillBucket = useSmartFillBucketGeneration(workspace.id);

  // V2 is the only schema version where stopping a running workspace is a
  // supported mutation; for older schemas we hide the affordance entirely.
  const canStop =
    props.canUpdate &&
    workspace.state === CaraWorkspaceState.Working &&
    workspace.schemaVersion === CaraWorkspaceSchemaVersion.V2;

  const titleEditable =
    props.canUpdate && workspace.title.trim().length > 0 && !rename.renameLoading;

  const updateWorkspaceVisibility = React.useCallback(
    async (visibility: VisibilityConfigInput) => {
      const res = await client.mutate<UpdateCaraWorkspacesMutation>({
        mutation: UpdateCaraWorkspacesDocument,
        variables: { workspaceIds: [workspace.id], input: { visibility } }
      });

      const applicationError = getApplicationError(new ApolloError({ graphQLErrors: res.errors }));
      const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: res.errors }));

      if (applicationError) {
        throw new Error(applicationError.message);
      }
      if (validationErrors?.length) {
        throw new Error(validationErrors[0].message);
      }

      const updated = res.data?.updateCaraWorkspaces?.find((w) => w.id === workspace.id);
      if (updated) {
        props.setWorkspace(updated);
      }
    },
    [workspace.id, props.setWorkspace]
  );

  return (
    <div className="flex w-full py-2 px-6 flex-row items-center justify-between gap-4 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0 sticky top-16 lg:top-0 z-20">
      <div className="flex flex-row items-center gap-2 min-w-0 flex-1">
        <Link
          to="/app/workspaces"
          aria-label="Back to workspaces"
          className="rounded-full p-2 flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-white/10 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-all duration-150 cursor-pointer shrink-0"
        >
          <ChevronLeftIcon className="size-4" />
        </Link>
        <WorkspaceTopBarTitle
          workspace={workspace}
          onRename={rename.renameCaraWorkspace}
          disabled={!titleEditable}
          editRequest={renameRequestNonce}
        />
      </div>
      <div className="flex flex-row items-center gap-3 shrink-0">
        <WorkspaceClientPicker
          workspaceId={workspace.id}
          clientName={workspace.metadata?.clientName}
        />
        <WorkspaceTopBarStatus timer={timer} />
        {visibilityEditAllowed && props.canUpdate ? (
          <VisibilityPopover visibility={workspace.visibility} onSave={updateWorkspaceVisibility} />
        ) : null}
        <WorkspaceTopBarActions
          canUpdate={props.canUpdate}
          canStop={canStop}
          onStop={props.onStop}
          stopLoading={props.stopLoading}
          onRename={() => setRenameRequestNonce((n) => n + 1)}
          renameDisabled={!titleEditable}
          onArchive={() => setShowArchiveDialog(true)}
          onConvertToPreset={
            convertToPresetEnabled && canCreatePreset ? () => setShowConvertDialog(true) : undefined
          }
          onOpenSmartFillBucket={canGenerateSmartFillBucket ? smartFillBucket.start : undefined}
        />
      </div>
      <WorkspaceTopBarArchiveDialog
        open={showArchiveDialog}
        loading={archive.archiveLoading}
        error={archive.archiveError}
        onCancel={() => setShowArchiveDialog(false)}
        onConfirm={archive.archiveCaraWorkspace}
      />
      {convertToPresetEnabled && (
        <ConvertToPresetWizard
          workspace={workspace}
          open={showConvertDialog}
          onClose={() => setShowConvertDialog(false)}
        />
      )}
      {canGenerateSmartFillBucket && <GenerateSmartFillBucketDialog {...smartFillBucket} />}
    </div>
  );
};
